import $modal from '@core/services/modal';
import api from '@core/services/api/_api';
import CustomerRecord from '@core/classes/Customer/CustomerRecord';
import DocumentAssociation from '@core/classes/DocumentAssociation';
import ENUMS from '@core/classes/Enums';
import modalPlaidIDVOverride from '@core/modals/modalPlaidIDVOverride.vue';
import { PlaidCustomerInfo } from '@core/classes/Plaid/PlaidCustomerInfo';
import PlaidGenerateLinkRequest from '@core/classes/Plaid/PlaidGenerateLinkRequest';
import PlaidLinkConfig from '@core/classes/Plaid/PlaidLinkConfig';
import { PlaidUserInfo } from '@core/classes/Plaid/PlaidUserInfo';
import settings from 'settings';
import util from '@core/services/util';

/**
 * Checks if the given customers pass the store's IDV validation rules. If anyone doesn't pass, the skip modal will be opened.
 * @param {store} object The current store that's running the IDV check.
 * @param {FIMenuCustomer[]} customers A list of all customers to verify
 * @param {string} employeeCode Code of the employee that will be recorded on the Skip reason (if applicable)
 * @param {string} employeeName Name of the employee that will be recorded on the Skip reason (if applicable)
 * @param {Function} postFunction Post function sent to the IDV Skip Modal
 * @returns {boolean} Whether ALL customers pass the IDV validation rules. If `false` is returned, then the skip modal has been opened.
 */
export async function checkForCustomerIdv(
	store: any,
	customers: any[],
	employeeCode: string,
	employeeName: string,
	postFunction: (() => void) | null = null,
	preventOpenSkipModal: boolean = false,
): Promise<boolean> {
	// Don't run check if IDV not enabled
	if (!store.storeSettings.isCustomerPlaidIDVEnabled) return true;

	// Can ignore if customer is BUSINESS, SKIPPED IDV, or SESSION IS SUCCESSFUL
	const _canIgnoreCustomer = (customer: any): boolean => {
		return customer.isEntity || customer.isIDVSkipped || customer.statusIDV === ENUMS.PLAID_VERIFICATION_STATUS.Success.ind;
	};

	// If we can ignore every customer, no need to do anything
	if (customers.every(customer => _canIgnoreCustomer(customer))) return true;

	// Only grabbing IDV sessions from customers that can't be ignored
	const customersToUpdate = customers.filter((customer: any) => customer.id && !_canIgnoreCustomer(customer));

	// Customer is verified only if we can find a session with the status "Success"
	const _isCustomerVerified = async (customer: any): Promise<{ isVerified: boolean; customer: any }> => {
		const response = await api.plaid.getNewestIdentityVerification(customer.id);
		// If an empty object was returned, there was never a session started
		if (Object.keys(response.data).length <= 0) {
			console.error('No existing identity verification for this customer');
			util.toastr('error', 'Error Retrieving Newest Identity Verification', 'No existing identity verification for this customer.');
			return { isVerified: false, customer };
		}
		// If there was an error from Plaid
		if (response?.data?.plaidResponse.error) {
			console.error('error', response?.data?.plaidResponse.error.errorMessage);
			util.toastr('error', 'Error Retrieving Newest Identity Verification', response?.data?.plaidResponse.error.errorMessage);
		}

		const latestSession = response?.data?.plaidResponse;

		if (!latestSession) return { isVerified: false, customer };

		return {
			isVerified: latestSession.status === ENUMS.PLAID_VERIFICATION_STATUS.Success.str,
			customer,
		};
	};

	const taskList: Promise<{ isVerified: boolean; customer: any }>[] = [];
	customersToUpdate.forEach((customer: any) => {
		taskList.push(_isCustomerVerified(customer));
	});

	const customersWithVerification = (await Promise.allSettled(taskList)).map(t => (t.status === 'fulfilled' ? t.value : { isVerified: false, customer: null }));

	if (customersWithVerification.some(c => !c.isVerified)) {
		if (!preventOpenSkipModal) {
			// if they Are authorized to skip, let them
			const customersToOverride = customersWithVerification.filter(a => !a.isVerified).map(b => b.customer);
			openPlaidIdvOverrideModal(customersToOverride, employeeCode, employeeName, postFunction);
		} else if (preventOpenSkipModal) {
			// if we are preventing from opening the skip modal (they don't have permission)
			util.toastr('error', 'Error', 'You are unauthorized to skip IDV. Please contact your manager to do so.', 20000);
		}

		return false;
	}

	return true;
}

/**
 * Opens the IDV skip modal
 * @param {FIMenuCustomer[]} customers A list of customers who are skipping the IDV session
 * @param {string} employeeCode The code of the employee who will provide the skip reason
 * @param {string} employeeName The name of the employee who will provide the skip reason
 * @param {Function} [postFunction=null] An optional post function to run after the Skip Modal has been closed
 */
export function openPlaidIdvOverrideModal(customers: any, employeeCode: any, employeeName: any, postFunction: any = null) {
	const customerInfo: any[] = [];

	customers.forEach((customer: any) => {
		customerInfo.push({
			type: ENUMS.PERSON_TYPES.CUSTOMER,
			name: customer.fullName ?? customer.getFullName(),
			id: customer.id,
		});
	});

	$modal.open(modalPlaidIDVOverride, {
		name: 'modalPlaidIDVOverride',
		passedData: {
			customerInfo,
		},
		postFunction: async (skipReasons: any) => {
			if (!skipReasons || skipReasons.length < 1) return;

			for (let r of skipReasons) {
				const override = {
					isIDVSkipped: true,
					skipReason: r.skipReason,
					skippedBy: { employeeNumber: employeeCode, employeeName },
					timestamp: new Date(Date.now()),
				};

				const customer = customers.find((cust: any) => cust.id == r.id);
				customer.idvOverride = customer.idvOverride.filter((x: any) => x.isIDVSkipped == true);
				customer.idvOverride.push(override);

				// We usually save in the post function; to be able to provide loading feedback to the FE.
			}

			if (postFunction?.constructor?.name === 'AsyncFunction') {
				// If it's asynchronous, await it
				await postFunction();
			} else if (postFunction != null) {
				// If it's not asynchronous, execute it directly
				postFunction();
			}
		},
	});
}

export async function getCustomersMostRecentIDVStatus(customer: CustomerRecord) {
	const id: string = await getIDVSessionID(customer.id);
	const plaidIDVResponse = await getIDVSession(true, id, customer);

	if (!plaidIDVResponse) return;
}

/**
 * Gets the most recent IDV session ID for a given customer.
 * @param {string} customerId The ID of the customer to lookup
 * @return {string} The most recent IDV session ID (if any were found)
 */
export async function getIDVSessionID(customerId: string) {
	try {
		const response = await api.plaid.getMostRecentIDVSessionID(customerId, ENUMS.DOC_ASSOCIATION.Customer);

		if (response.data) {
			return response.data;
		} else return null;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Gets the IDV Session information from Plaid.
// Wether we should update customer
export async function getIDVSession(updateCustomer = false, sessionId: any = null, customer: CustomerRecord) {
	try {
		// If we don't pass in a session id, lets grab it ourselves.
		if (sessionId == null) {
			sessionId = await getIDVSessionID(customer.id);
			if (sessionId == null) return null;
		}

		const response = await api.plaid.getIdentityVerification(sessionId, customer.storeCodes[0]);
		if (response?.data.error) {
			console.error('PLAID API ERROR.', response?.data.error);
			util.toastr('error', response?.data.error.errorType, response?.data.error.errorMessage);
			return null;
		}
		if (!response?.data) return null;

		const plaidResponse = response.data.plaidResponse;

		if (updateCustomer && plaidResponse) {
			// If we get accepted, declined, etc. Anything but in progress, then lets mark it as not skipped.
			if (plaidResponse.status !== ENUMS.PLAID_VERIFICATION_STATUS.Active.str) customer.isIDVSkipped = false;

			customer.statusIDV = ENUMS.PLAID_VERIFICATION_STATUS.stringToIndex(plaidResponse.status);
		}

		return response.data;
	} catch (err) {
		console.error(err);
		return null;
	}
}

/**
 * @param {CustomerRecord} customer
 * @param {string} storeCode
 * @method Generates a plaid portal link
 */
export async function generatePlaidPortalLink(customer: CustomerRecord) {
	try {
		const request = await generateMinimumParametersForPlaidPage(customer, customer.storeCodes[0]);

		const link = await request.getPortalLink();

		// window.location.href = generateLinkResponse.data; // opens in the current tab
		window.open(link, '_blank'); //opens new tab
	} catch (err) {
		console.error('ERROR: ', err);
	}
}

export async function generateMinimumParametersForPlaidPage(customer: CustomerRecord, storeCode: string) {
	// Create Plaid Config
	const plaidUser = new PlaidUserInfo({
		emailAddress: customer.emailAddress,
		phoneNumber: '+1' + customer.phoneNumber, // Format: E.164 (ex. +11234567890)
		dateOfBirth: customer.dateOfBirth, // moment(customer.dateOfBirth, "YYYY-MM-DDTHH:mm:ss").format("YYYY-MM-DD"), // Format: YYYY-MM-DD
		name: {
			givenName: customer.firstName,
			familyName: customer.lastName,
		},
		address: {
			street: customer.addresses[0].addressLine1,
			street2: customer.addresses[0].addressLine2,
			city: customer.addresses[0].city,
			region: customer.addresses[0].state, // ISO 3166-2 subdivision code (ex. state, province, prefecture, zone, etc.)
			postalCode: customer.addresses[0].zipCode,
			country: customer.addresses[0].country, // Format: ISO 3166-1 alpha-2 two-letter code
		},
		idNumber: null,
		isIdEncrypted: false,
	});

	const plaidUserInfo = settings.plaidEnvironment !== 'SANDBOX' ? plaidUser : PlaidUserInfo.getSandboxUser();

	const plaidConfig: PlaidLinkConfig = new PlaidLinkConfig({
		name: 'Saturn',
		countries: [ENUMS.PLAID_COUNTRIES.UnitedStates],
		linkLanguage: ENUMS.PLAID_LANGUAGES.English,
		productsToInit: [ENUMS.PLAID_PRODUCTS.IdentityVerification],
		userData: plaidUserInfo,
	});

	// Generate Link Token
	const prepopulatedResponse = await plaidConfig.prepopulateIdentityVerification(customer.id, storeCode);
	if (!prepopulatedResponse?.data || prepopulatedResponse.data.error) {
		const errorCause = prepopulatedResponse?.data?.error ?? 'No response from server.';
		throw new Error('An error occurred when pre-populating the IDV session.', {
			cause: errorCause,
		});
	}

	const plaidCustomerInfo = new PlaidCustomerInfo();
	plaidCustomerInfo.customerId = customer.id;
	const linkTokenResponse = await plaidConfig.createLinkToken(plaidCustomerInfo);

	const linkToken = linkTokenResponse.data.linkToken;

	// Create Document Association
	const customerAssociation: DocumentAssociation = new DocumentAssociation({
		id: customer.id,
		type: ENUMS.DOC_ASSOCIATION.Customer,
	});

	const request = new PlaidGenerateLinkRequest(linkToken, [customerAssociation], customer.storeCodes[0]);
	return request;
}

const PlaidHelper = {
	getIDVSession,
	getIDVSessionID,
	checkForCustomerIdv,
	generatePlaidPortalLink,
	openPlaidIdvOverrideModal,
	getCustomersMostRecentIDVStatus,
	generateMinimumParametersForPlaidPage,
};

export default PlaidHelper;
